import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { apiEndpoints } from 'src/app/api-endpoints';
import { Store, select } from '@ngrx/store';
import { BoardState } from '../board/+store/board-state/board-state.reducer';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { ViewDefinition } from '../board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import {
  CustomViewDto,
  CustomViewsDto,
  CustomViewListItem,
  CustomViewIdRequest,
  ApplicationIdRequest,
  convertCustomViewDtoToModel,
  convertCustomViewModelToDto,
  generateViewId,
  validateViewDefinition
} from './models/custom-view.models';

@Injectable({
  providedIn: 'root'
})
export class CustomViewService {
  private apiUrl = environment.apiConfig.baseUri + apiEndpoints.customView;
  private customViewApis = {
    createCustomView: '/create-custom-view',
    updateCustomView: '/update-custom-view',
    deleteCustomView: '/delete-custom-view',
    getCustomViews: '/get-custom-views',
    getCustomView: '/get-custom-view',
    getUserViews: '/get-user-views',
  };

  constructor(
    private http: HttpClient,
    private boardStore: Store<BoardState>
  ) {}

  /**
   * Get all custom views for the current application
   */
  getCustomViews(applicationId: number): Observable<CustomViewListItem[]> {
    return this.http.post<CustomViewsDto>(
      `${this.apiUrl}${this.customViewApis.getCustomViews}`,
      new ApplicationIdRequest(applicationId)
    ).pipe(
      filter(response => response !== null),
      map(response => response.customViews.map(view => ({
        viewId: view.viewId!,
        viewName: view.viewName,
        viewDescription: view.viewDescription,
        isActive: view.isActive,
        isShared: view.isShared,
        displayOrder: view.displayOrder,
        createdBy: view.createdBy || 'Unknown',
        modifiedDate: view.modifiedDate || new Date()
      }))),
      catchError(this.handleError)
    );
  }

  /**
   * Get a specific custom view by ID
   */
  getCustomView(viewId: number): Observable<ViewDefinition> {
    return this.http.post<CustomViewDto>(
      `${this.apiUrl}${this.customViewApis.getCustomView}`,
      new CustomViewIdRequest(viewId)
    ).pipe(
      filter(response => response !== null),
      map(dto => convertCustomViewDtoToModel(dto)),
      catchError(this.handleError)
    );
  }

  /**
   * Create a new custom view
   */
  createCustomView(applicationId: number, viewDefinition: ViewDefinition): Observable<ViewDefinition> {
    const dto = convertCustomViewModelToDto(applicationId, viewDefinition);

    return this.http.post<CustomViewDto>(
      `${this.apiUrl}${this.customViewApis.createCustomView}`,
      dto
    ).pipe(
      map(responseDto => convertCustomViewDtoToModel(responseDto)),
      catchError(this.handleError)
    );
  }

  /**
   * Update an existing custom view
   */
  updateCustomView(viewId: number, applicationId: number, viewDefinition: ViewDefinition): Observable<ViewDefinition> {
    const dto = convertCustomViewModelToDto(applicationId, viewDefinition, viewId);

    return this.http.post<CustomViewDto>(
      `${this.apiUrl}${this.customViewApis.updateCustomView}`,
      dto
    ).pipe(
      map(responseDto => convertCustomViewDtoToModel(responseDto)),
      catchError(this.handleError)
    );
  }

  /**
   * Delete a custom view
   */
  deleteCustomView(viewId: number): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.apiUrl}${this.customViewApis.deleteCustomView}`,
      new CustomViewIdRequest(viewId)
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get active views for a user (personal and shared)
   */
  getActiveViewsForUser(applicationId: number, userId: string): Observable<CustomViewListItem[]> {
    return this.http.post<CustomViewsDto>(
      `${this.apiUrl}${this.customViewApis.getUserViews}`,
      new ApplicationIdRequest(applicationId)
    ).pipe(
      filter(response => response !== null),
      map(response => response.customViews
        .filter(view => view.isActive)
        .map(view => ({
          viewId: view.viewId!,
          viewName: view.viewName,
          viewDescription: view.viewDescription,
          isActive: view.isActive,
          isShared: view.isShared,
          displayOrder: view.displayOrder,
          createdBy: view.createdBy || 'Unknown',
          modifiedDate: view.modifiedDate || new Date()
        }))),
      catchError(this.handleError)
    );
  }

  /**
   * Clone an existing view
   */
  cloneCustomView(viewId: number, newName: string): Observable<ViewDefinition> {
    return this.getCustomView(viewId).pipe(
      map(view => {
        // Create a deep copy and update properties
        const clonedView = JSON.parse(JSON.stringify(view)) as ViewDefinition;
        // Clear the id so the backend assigns a new one when saved via createCustomView
        clonedView.id = '';
        clonedView.name = newName;
        clonedView.lastModified = new Date();
        return clonedView;
      }),
      catchError(this.handleError)
    );
  }




  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    console.error('CustomViewService error:', error);
    return throwError(() => new Error('Error processing custom view request'));
  }

  /**
   * Validate a view definition has all required fields
   */
  validateViewDefinition(viewDef: ViewDefinition): boolean {
    return validateViewDefinition(viewDef);
  }

  /**
   * Export view definition as JSON file
   */
  exportViewDefinition(viewDef: ViewDefinition): void {
    const dataStr = JSON.stringify(viewDef, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${viewDef.name.replace(/\s+/g, '_')}_view_definition.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Import view definition from JSON
   */
  importViewDefinition(jsonString: string): ViewDefinition {
    try {
      const viewDef = JSON.parse(jsonString) as ViewDefinition;

      // Validate the imported definition
      if (!validateViewDefinition(viewDef)) {
        throw new Error('Invalid view definition structure');
      }

      // Generate new IDs to avoid conflicts
      viewDef.id = generateViewId();
      viewDef.lastModified = new Date();

      return viewDef;
    } catch (error) {
      console.error('Error importing view definition:', error);
      throw new Error('Failed to import view definition');
    }
  }
}
